<template>
        <v-card class="ma-4 mt-2">
            <v-row class="pl-3" align="center">
                <v-col>
                    <v-card-title class="pa-0">{{ title }}</v-card-title>
                </v-col>

                <v-spacer></v-spacer>

                <v-col class="d-flex justify-end" cols="auto">
                    <v-tooltip top>
                        <template v-slot:activator="{ on }">
                            <v-btn
                            v-on="on"
                            text
                            color="primary"
                            @click="showFlowDiagram"
                            class="me-2"
                            >
                            <v-icon large>mdi-state-machine</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t("FlowChart") }}</span>
                    </v-tooltip>
                    <v-btn @click="cancel">
                    {{ $t("Cancel") }}
                    </v-btn>
                    <v-btn color="primary" @click="saveStates" :loading="loading">
                    {{ $t("Save") }}
                    </v-btn>
                </v-col>
            </v-row>
            <v-card-text class="pt-0">
                <v-row justify="center" align="center" v-if="loading">
                    <div class="text-center">
                    <div>
                        <v-progress-circular
                        class="my-4"
                        :size="50"
                        width="5"
                        color="primary"
                        indeterminate
                        ></v-progress-circular>
                    </div>
                    <p class="display-1">{{ $t("Loading") }}...</p>
                    </div>
                </v-row>
                <v-row justify="center" v-if="!loading">
                <v-expansion-panels>
                    <v-expansion-panel
                    v-for="(state, statesIndex) in states"
                    :key="statesIndex"
                    >
                    <v-expansion-panel-header>
                        <template v-slot:default="{ open }">
                        <v-row no-gutters>
                            <v-col>
                            <v-fade-transition leave-absolute>
                                <span v-if="open" key="0">
                                {{ `${$t("DepartureState")}: ${state.description}` }}
                                </span>
                                <span v-else key="1">
                                {{ state.description }}
                                </span>
                            </v-fade-transition>
                            </v-col>
                        </v-row>
                        </template>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="pa-2">
                        <v-row>
                        <v-col align-self="center">
                            <v-switch
                            v-model="state.notifyOnStateChange"
                            :label="$t('Site.Flows.AlertOnStateChange')"
                            ></v-switch>
                        </v-col>
                        <v-col align-self="center">
                            <v-switch
                            v-model="state.notifyOnExpiration"
                            :label="$t('AlertOnExpiration')"
                            ></v-switch>
                        </v-col>
                        <v-col
                            cols="2"
                            align-self="center"
                            v-if="state.notifyOnExpiration"
                        >
                            <v-text-field
                            v-model="state.numberOfDaysNotif"
                            :label="$t('Days')"
                            min="1"
                            step="1"
                            type="number"
                            ></v-text-field>
                        </v-col>
                        <v-col v-if="!flow.activeNotif" class="warning--text font-italic" align-self="center">
                            {{ $t("FlowNotificationsDisabledWarning") }}
                        </v-col>
                        </v-row>
                        <v-row
                        v-if="state.transitions.length == 0"
                        justify="center"
                        align="center"
                        >
                        <v-col cols="1">
                            <v-btn
                            color="primary"
                            prepend-icon="mdi-plus"
                            outlined
                            @click="addStateTransitions(state)"
                            >
                            {{ $t("Add") }}
                            </v-btn>
                        </v-col>
                        </v-row>

                        <div
                        v-for="(item, transitionsIndex) in state.transitions"
                        :key="transitionsIndex"
                        >
                        <v-row align="center">
                            <div class="ma-0 pa-0">
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                <v-btn class="text-center ma-0 pa-0"
                                    v-on="on"
                                    large
                                    color="error"
                                    icon
                                    @click="removeStateTransitions(state, item.uuid)"
                                >
                                    <v-icon class="text-center ma-0 pa-0">mdi-delete</v-icon>
                                </v-btn>
                                </template>
                                <span>{{ $t("Delete") }}</span>
                            </v-tooltip>
                            </div>
                            <v-col>
                            <v-select
                                :label="$t('TransitionType')"
                                v-model="item.transitionType"
                                :items="transitionTypes"
                                item-text="text"
                                item-value="id"
                            ></v-select>
                            </v-col>
                            <v-col>
                            <v-text-field
                                readonly
                                v-model="item.displayConditionStr"
                                :label="$t('Condition')"
                                :append-icon="
                                item.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'
                                "
                                @click:append="editCondition(item)"
                            ></v-text-field>
                            </v-col>
                            <v-col>
                            <v-select
                                :label="$t('DestinationState')"
                                v-model="item.stateTo"
                                :items="states"
                                item-text="description"
                                item-value="id"
                            ></v-select>
                            </v-col>
                            <v-col>
                            <v-select
                                multiple
                                :label="$t('Roles')"
                                v-model="item.roleTypeIDs"
                                :items="rolesList"
                                item-text="text"
                                item-value="id"
                                :error-messages="roleErrors(statesIndex, transitionsIndex)"
                            ></v-select>
                            </v-col>
                            <v-col v-if="item.transitionType === 'V'">
                            <v-text-field
                                type="number"
                                min="1"
                                step="1"
                                :label="$t('Site.Flows.TotalRequiredValidations')"
                                v-model="item.totalRequiredValidations"
                            ></v-text-field>
                            </v-col>
                            <v-col>
                            <v-select
                                :label="$t('DigitalSignature')"
                                clearable
                                v-model="item.digitalSignatureConfigId"
                                :items="digitalSignaturesList"
                                item-text="text"
                                item-value="id"
                            ></v-select>
                            </v-col>
                            <v-col>
                            <v-btn
                                color="blue darken-4"
                                outlined
                                @click="editActions(item)"
                            >
                                {{ $t("Actions") }}
                                <v-icon v-if="item.actions.length > 0" right>
                                mdi-pencil
                                </v-icon>
                                <v-icon v-if="item.actions.length == 0" right>
                                mdi-plus
                                </v-icon>
                            </v-btn>
                            </v-col>
                            <v-col>
                                <v-select
                                    :label="$t('Site.Flows.ConvertTo')"
                                    v-model="item.convertTo"
                                    :items="convertExtensions"
                                    item-text="text"
                                    item-value="id"
                                ></v-select>
                            </v-col>
                            <div class="ma-0 pa-0">
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                <v-btn
                                    v-on="on"
                                    large
                                    color="primary"
                                    icon
                                    class="ma-0 pa-0"
                                    @click="addStateTransitions(state)"
                                >
                                    <v-icon>mdi-plus</v-icon>
                                </v-btn>
                                </template>
                                <span>{{ $t("Add") }}</span>
                            </v-tooltip>
                            </div>
                        </v-row>
                        </div>
                    </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
                </v-row>
                <SearchCriteriaBuilderModal
                :ref="conditionModalKey"
                :contentTypeIDs="flow.contentTypeIDs"
                @on-condition-saved="onConditionChanged"
                :allowEmptyCondition="true"
                />
                <ActionsModal
                :ref="actionsModalKey"
                :contentTypeIDs="flow.contentTypeIDs"
                @on-saved="onActionsSaved"
                />
                <FlowDiagramModal :ref="flowDiagramModalKey" />
            </v-card-text>
        </v-card>
</template>
<script>
import SearchCriteriaBuilderModal from '@/components/search/SearchCriteriaBuilderModal.vue';
import ActionsModal from '@/components/administration/flow/ActionsModal.vue';
import FlowDiagramModal from '@/components/administration/flow/FlowDiagramModal.vue';
import Flow from '@/ecm/models/workflow/Flow';
import State from '@/ecm/models/workflow/State';
import StateTransition from '@/ecm/models/workflow/StateTransition';
import FlowService from '@/services/FlowService';
import RoleService from '@/services/RoleService';
import DigitalSignatureService from '@/services/DigitalSignatureService';
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

export default {
    name: 'States',
    components: {
        SearchCriteriaBuilderModal,
        ActionsModal,
        FlowDiagramModal,
    },
    mounted() {
        this.loading = true;
        this.getRoles();
        this.getDigitalSignatures();
        this.$v.$reset();
        this.flowStateId = this.$route.params.id;
        FlowService.getFlowById(this.flowStateId).then((data) => {
            this.flow = new Flow(data);
            this.title = this.$t('States') + ' - ' + this.flow.name;
            this.getStates();
            this.loading = false;
        });
    },
    data() {
        return {
            title: '',
            flow: new Flow(),
            open: false,
            conditionModalOpen: false,
            states: [],
            originalStates: [],
            currentTransition: new StateTransition(),
            rolesList: [],
            transitionTypes: [
                { id: 'V', text: this.$t('Validate') },
                { id: 'R', text: this.$t('Refuse') },
            ],
            digitalSignaturesList: [],
            conditionModalKey: 'conditionModalKey',
            actionsModalKey: 'actionModalKey',
            flowDiagramModalKey: 'flowDiagramModalKey',
            flowStateId: 0,
            loading: false,
            convertExtensions: [
                { id: 1, text: `${this.$t('Site.Flows.Original')}` },
                { id: 2, text: 'PDF' },
                { id: 3, text: 'DOCX' }
            ],
            showWarning: false,
        };
    },
    mixins: [validationMixin],
    validations: {
        states: {
            $each: {
                transitions: {
                    $each: {
                        roleTypeIDs: { required },
                        stateTo: {},
                    },
                },
            },
        },
    },
    computed: {
        conditionModalInstance() {
            return this.$refs[this.conditionModalKey];
        },
        actionsModalInstance() {
            return this.$refs[this.actionsModalKey];
        },
        flowDiagramModalInstance() {
            return this.$refs[this.flowDiagramModalKey];
        },
    },
    methods: {
        roleErrors(statesIndex, transitionsIndex) {
            const errors = [];
            if (
                !this.$v.states.$each[statesIndex].transitions.$each[transitionsIndex]
                    .roleTypeIDs.$dirty
            )
                return errors;
            !this.$v.states.$each[statesIndex].transitions.$each[transitionsIndex]
                .roleTypeIDs.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        editCondition(item) {
            this.currentTransition = item;
            this.conditionModalInstance.edit(item.conditionStr);
        },
        onConditionChanged(condition) {
            this.currentTransition.conditionStr = condition.conditionStr;
            this.currentTransition.displayConditionStr =
                condition.displayConditionStr;
        },
        editActions(item) {
            this.currentItem = item;
            this.actionsModalInstance.edit(item.actions);
        },
        showFlowDiagram() {
            this.flowDiagramModalInstance.fromFlow(this.flow.name, this.flow.id, this.states);
        },
        onActionsSaved(actions) {
            this.currentItem.actions = actions;
        },
        getStates() {
            FlowService.getStates(this.flow.id).then((data) => {
                this.states = data.map((dto) => new State(dto));
                this.originalStates = data.map((dto) => new State(dto));
            });
        },
        getRoles() {
            RoleService.getList().then((data) => {
                this.rolesList = data;
            });
        },
        getDigitalSignatures() {
            DigitalSignatureService.getList().then((data) => {
                this.digitalSignaturesList = data;
            });
        },
        async saveStates() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }
            this.showWarning = this.shouldShowWarning(this.originalStates, this.states);
            if (this.showWarning) {
                var res = await this.$confirm(this.$t('Site.Flows.SaveWorkflowWarning'), {
                    title: this.$t('Confirm'),
                    icon: 'mdi-alert',
                    color: 'warning',
                    buttonTrueText: this.$t('Save'),
                    buttonFalseText: this.$t('Cancel'),
                });
                if (res != 1) {
                    return;
                }
            }

            this.loading = true;

            var states = this.states.map((item) => item.toDto());
            FlowService.saveStates(states)
                .then(() => {
                    this.$router.push('/administration/flows');
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.loading = false;
                });
        },
        addStateTransitions(state) {
            var newTransition = new StateTransition();
            newTransition.roleTypeIDs = [this.rolesList[0].id];
            var stateTo = this.states.find((x) => x.id != state.id);
            if (!stateTo) {
                stateTo = state;
            }
            newTransition.stateTo = stateTo.id;
            newTransition.convertTo = this.convertExtensions[0].id;
            state.transitions.push(newTransition);
        },
        removeStateTransitions(state, uuid) {
            state.transitions = state.transitions.filter(
                (item) => item.uuid !== uuid
            );
        },
        cancel() {
            this.$router.push('/administration/flows');
        },
        shouldShowWarning(originalStates, currentStates) {
            const currById = new Map(currentStates.map(s => [s.id, s]));

            for (const origState of originalStates) {
                const currState = currById.get(origState.id);
                if (!currState) continue;

                const origHasMulti = this.hasMultiValidations(origState.transitions);

                const origMap = new Map((origState.transitions || []).map(t => [this.trKey(t), t]));
                const currMap = new Map((currState.transitions || []).map(t => [this.trKey(t), t]));

                if (origHasMulti) {
                    for (const key of currMap.keys()) {
                        if (!origMap.has(key)) return true;
                    }
                    for (const key of origMap.keys()) {
                        if (!currMap.has(key)) return true;
                    }

                    for (const [key, oTr] of origMap) {
                        const cTr = currMap.get(key);
                        if (!cTr) continue;

                        if (oTr.transitionType !== cTr.transitionType) return true;
                        if ((oTr.conditionStr || '') !== (cTr.conditionStr || '')) return true;
                        if (oTr.stateTo !== cTr.stateTo) return true;
                        if (!this.equalSets(oTr.roleTypeIDs, cTr.roleTypeIDs)) return true;

                        const oldV = oTr.totalRequiredValidations;
                        const newV = parseInt(cTr.totalRequiredValidations);
                        if ((oldV === 1 && newV > 1) || (oldV > 1 && newV > 1 && oldV !== newV) || (oldV > 1 && newV === 1)
                        ) {
                            return true;
                        }
                    }
                }
            }
            return false;
        },
        asSet(arr = []) {
            return new Set(arr.map(x => x + '')); // normalizo a string
        },
        equalSets(a = [], b = []) {
            const A = this.asSet(a);
            const B = this.asSet(b);
            if (A.size !== B.size) return false;
            for (const v of A) if (!B.has(v)) return false;
            return true;
        },
        hasMultiValidations(transitions = []) {
            return (transitions || []).some(t => t.totalRequiredValidations > 1);
        },
        trKey(t) {
            return (t.id && t.id > 0) ? `id:${t.id}` : `uuid:${t.uuid}`;
        }
    },
};
</script>
